@file:OptIn(ExperimentalJsExport::class)

package nl.astraeus.wiki.components

import kotlinx.browser.window
import kotlinx.html.*
import nl.astraeus.komp.HtmlBuilder
import nl.astraeus.komp.Komponent
import nl.astraeus.komp.currentElement
import nl.astraeus.wiki.parser.MarkdownPart
import nl.astraeus.wiki.service.ResourceService
import nl.astraeus.wiki.service.ResourceType
import org.w3c.dom.Node

private var graphId = 0

@JsExport
data class MermaidOptions(
  @JsName("startOnLoad")
  val startOnLoad: Boolean = true,
  @JsName("logLeveL")
  val logLeveL: String = "info",
)

fun HtmlBuilder.renderPart(part: MarkdownPart) = when (part) {
  is MarkdownPart.Header -> {
    when (part.size) {
      1 -> h1 {
        +part.text
      }

      2 -> h2 {
        +part.text
      }

      3 -> h3 {
        +part.text
      }

      4 -> h4 {
        +part.text
      }

      5 ->
        h5 {
          +part.text
        }

      else -> {
        span {
          +part.text
        }
      }
    }
  }

  is MarkdownPart.Ruler -> hr {}

  is MarkdownPart.UnorderedList -> ul {
    for (line in part.lines) {
      if (line.isNotBlank()) {
        li {
          +line
        }
      }
    }
  }

  is MarkdownPart.OrderedList -> ol {
    for (line in part.lines) {
      if (line.isNotBlank()) {
        li {
          +line
        }
      }
    }
  }

  is MarkdownPart.CodeBlock -> {
    if (part.language == "mermaid") {
      pre("mermaid") {
        +part.text
        ResourceService.loadResource(ResourceType.JS, "mermaid.min.js") {
          try {
            val errorHandler = { err: dynamic, hash: dynamic ->
              console.log("Error: ", err, hash)
            }
            window.asDynamic().mermaid.parseError = errorHandler
            window.asDynamic().mermaid.run()
          } catch (e: Throwable) {
            console.error(e)
          }
        }
      }
    } else if (part.language == "viz") {
      div {
        val element = currentElement()

        ResourceService.loadResource(ResourceType.JS, "viz.js") {
          window.asDynamic().Viz.instance().then { viz ->
            element.appendChild(viz.renderSVGElement(part.text) as Node)
          }
        }
      }
    } else if (part.language == "jax") {
      div {
        val element = currentElement()

        ResourceService.loadResource(
          ResourceType.JS,
          "https://cdn.jsdelivr.net/npm/mathjax@3/es5/tex-svg.js"
        ) { //""tex-mml-chtml.js") {
          val mathElement: Node = window.asDynamic().MathJax.tex2svg(part.text) as Node
          //console.log("Jax", mathElement)
          element.appendChild(mathElement)
        }
      }
    } else {
      pre {
        code(classes = "language-${part.language}") {
          +part.text

          val element = currentElement()
          ResourceService.loadResource(ResourceType.CSS, "styles/arduino-light.min.css")
          //ResourceService.loadResource(ResourceType.CSS, "styles/androidstudio.min.css")
          ResourceService.loadResource(ResourceType.JS, "highlight.js") {
            ResourceService.loadResource(ResourceType.JS, "languages/${part.language}.min.js") {
              window.asDynamic().hljs.highlightElement(element)
            }
          }
        }
      }
    }
  }

  is MarkdownPart.NewLine -> span { }

  is MarkdownPart.Paragraph -> p {
    renderParagraph(part)
  }

  is MarkdownPart.Table -> table("table table-bordered") {
    thead {
      tr {
        for (h in part.headers) {
          th { +h }
        }
      }
    }
    tbody {
      for ((index, row) in part.rows.withIndex()) {
        tr {
          if (index % 2 == 0) {
            classes += "even"
          } else {
            classes += "odd"
          }
          for (cell in row) {
            td { +cell }
          }
        }
      }
    }
  }

  MarkdownPart.PageBreak -> hr("page-break") {}
}

fun HtmlBuilder.renderParagraph(part: MarkdownPart.Paragraph) {
  for (part in part.parts) {
    when (part) {
      is MarkdownPart.ParagraphPart.Bold -> strong {
        +part.text
      }

      is MarkdownPart.ParagraphPart.BoldItalic -> strong {
        i {
          +part.text
        }
      }

      is MarkdownPart.ParagraphPart.Image -> if (part.url != null) {
        a {
          href = part.url
          img {
            src = part.src
            alt = part.alt
          }
        }
      } else {
        img {
          src = part.src
          alt = part.alt
        }
      }
      is MarkdownPart.ParagraphPart.Italic -> i {
        +part.text
      }

      is MarkdownPart.ParagraphPart.Link -> a {
        href = part.url
        +(part.label ?: part.url)
      }

      is MarkdownPart.ParagraphPart.Text -> span {
        +part.text
      }

      is MarkdownPart.ParagraphPart.InlineCode -> span("inline-code") {
        +part.text
      }

      is MarkdownPart.ParagraphPart.StrikeThrough -> span("strike-through") {
        +part.text
      }

      is MarkdownPart.ParagraphPart.LineBreak -> br {}
    }
  }
}

class MarkdownRenderer(
  val parts: List<MarkdownPart>
) : Komponent() {

  override fun HtmlBuilder.render() {
    div {
      for (part in parts) {
        renderPart(part)
      }
    }
  }
}